import React from 'react';
import { Shelf } from 'lib/modelBuilder/entities/Shelf';
import { ShelfSelection } from 'lib/modelBuilder/entities/ShelfSelection';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { createStructuredSelector } from 'reselect';
import { RootState } from 'containers/PanesPanel/types';
import { makeSelectIsVisibleColorsPanel } from 'containers/Configurator/selectors/general';
import { makeSelectShelvesSelectedLastCabinet } from 'containers/Configurator/selectors/selection';
import {
  makeSelectHaveFourDoors,
  makeSelectHaveFourDoorsOnly,
  makeSelectShelfSelectionProfile,
} from 'containers/Ribbon/ShelvesRibbon/selectors';
import { makeSelectLastHistoryCabinet } from 'containers/Configurator/selectors/history';
import { makeSelectFullDrawersSelection } from 'containers/DrawersPanel/selectors';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Position } from 'lib/modelBuilder/constants';
import { toggleSelectionPaneAction } from 'containers/Ribbon/ShelvesRibbon/actions';
import { addPieceAction } from 'containers/Configurator/actions/furniture';
import { PieceSelectionData } from 'containers/PanesPanel/constants';
import GenericObjectSelector, {
  SelectorSize,
} from 'components/GenericObjectSelector';
import { Color } from 'lib/modelBuilder/entities/Color';
import { setColorsPanelAction } from 'containers/Configurator/actions/general';
import useMedia from 'use-media';
import { ActivePanel } from '../../Configurator/constants/general';

interface StateProps {
  isVisible: boolean;
  shelvesSelected: Shelf[];
  shelfSelectionProfile: ShelfSelection;
  lastHistoryCabinet: Cabinet;
  isFullDrawersSelection: boolean;
  hasFourDoors: boolean;
  hasFourDoorsOnly: boolean;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  isVisible: makeSelectIsVisibleColorsPanel(ActivePanel.panes),
  shelvesSelected: makeSelectShelvesSelectedLastCabinet(),
  shelfSelectionProfile: makeSelectShelfSelectionProfile(),
  lastHistoryCabinet: makeSelectLastHistoryCabinet(),
  isFullDrawersSelection: makeSelectFullDrawersSelection(),
  hasFourDoors: makeSelectHaveFourDoors(),
  hasFourDoorsOnly: makeSelectHaveFourDoorsOnly(),
});

const SidesSelector = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const {
    shelvesSelected,
    shelfSelectionProfile,
    hasFourDoorsOnly,
  } = useSelector(stateSelector);
  const isMobile = useMedia({ maxWidth: 1023 });

  const panes: PieceSelectionData[] = [];
  panes.push({
    title: t('Left pane'),
    position: Position.Left,
    color: shelfSelectionProfile.getSelectedPaneColor(
      shelvesSelected,
      Position.Left,
    ),
  });
  if (!hasFourDoorsOnly) {
    panes.push({
      title: t('Back pane'),
      position: Position.Back,
      color: shelfSelectionProfile.getSelectedPaneColor(
        shelvesSelected,
        Position.Back,
      ),
    });
  }
  panes.push({
    title: t('Right pane'),
    position: Position.Right,
    color: shelfSelectionProfile.getSelectedPaneColor(
      shelvesSelected,
      Position.Right,
    ),
  });

  const mobileOnClick = (position: Position) => {
    // Selection of the panes
    dispatch(toggleSelectionPaneAction(position));
    // Open the panel to select the color
    dispatch(setColorsPanelAction(ActivePanel.panes));
  };

  const generateSelector = () => {
    if (isMobile) {
      return panes.map(selectionData => (
        <GenericObjectSelector
          key={selectionData.position}
          title={selectionData.title}
          color={selectionData.color as Color}
          onClick={() => {
            mobileOnClick(selectionData.position as Position);
          }}
          canAddNew={true}
          present={ShelfSelection.checkPanePresentAtPosition(
            selectionData.position as Position,
            shelvesSelected,
          )}
          size={SelectorSize.large}
        />
      ));
    } else {
      return panes.map(selectionData => (
        <GenericObjectSelector
          title={selectionData.title}
          key={selectionData.position}
          selected={shelfSelectionProfile.isPaneSelected(
            selectionData.position as Position,
          )}
          color={selectionData.color as Color}
          onClick={(e: any) => {
            const present = ShelfSelection.checkPanePresentAtPosition(
              selectionData.position as Position,
              shelvesSelected,
            );
            if (!present) {
              dispatch(
                addPieceAction('Pane', selectionData.position as Position),
              );
            }
            if (e.shiftKey || e.ctrlKey) {
              dispatch(
                toggleSelectionPaneAction(
                  selectionData.position as Position,
                  true,
                ),
              );
            } else
              dispatch(
                toggleSelectionPaneAction(selectionData.position as Position),
              );
          }}
          present={ShelfSelection.checkPanePresentAtPosition(
            selectionData.position as Position,
            shelvesSelected,
          )}
          canAddNew
          size={SelectorSize.small}
        />
      ));
    }
  };

  return (
    <div className="flex w-full justify-around lg:justify-start">
      {generateSelector()}
    </div>
  );
};

export default SidesSelector;
